import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { BookHalf, Funnel, FunnelFill, Sun, Moon } from "react-bootstrap-icons";
import { Typeahead } from "react-bootstrap-typeahead";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LineChart, Line, Treemap, ScatterChart, Scatter, ZAxis, ReferenceLine, LabelList } from "recharts";

import { getAnalyticsWidget, getGenres, createExportSchedule } from "../../../utils/Requests";
import { getAnalyticsBrandLabel, getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, DocsSidebarEntry, WidgetTheme } from "../../../types/analytics";
import Branding from "../../../config/Branding";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import HeatmapMetricsTable from "../../../components/analytics/HeatmapMetricsTable";
import { DocsSidebar, ExportButton, RankedBarDatum, RankedBarWidget, ScheduleExportModal, WidgetCard, PopPeriodBanner, DataFreshnessBanner, WidgetInfoButton } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";
import { truncateAxisLabelToWidth } from "../widgets/utils";
import {
    ALL_GENRES_VALUE,
    GenreExplorerMetricsByTimePoint,
    GenreExplorerTimeView,
    METRIC_BY_TIME_OPTIONS,
    formatMetricByTimeValue,
    getDefaultMetricByTime,
    getMetricByTimeOption,
    mapDowMetricsByTimeRows,
    mapWeeklyMetricsByTimeRows,
    toChartMetricValue
} from "./contentGenreExplorerMetricsByTime";

type ContentGenreExplorerProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ContentGenreExplorerState = {
    isGenresLoading: boolean;
    isLoading: boolean;
    error: string | null;
    widgetStates: WidgetStateMap;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    genres: string[];
    selectedGenres: string[];
    selectedMetricByTime: string;
    activeTimeView: GenreExplorerTimeView;
    metricsData: Record<string, any>[];
    topBrandsData: TopBrandPoint[];
    recsGenrePlaysData: RecsGenrePlayPoint[];
    risersFallersData: GenreRisersFallersDatum[];
    weeklyMetricsByTimeData: GenreExplorerMetricsByTimePoint[];
    dowMetricsByTimeData: GenreExplorerMetricsByTimePoint[];
    pageTableData: ContentPageTableRow[];
    railTableData: ContentRailTableRow[];
    deviceTableData: ContentDeviceTableRow[];
    isPageTableView: boolean;
    isRailTableView: boolean;
    isDeviceTableView: boolean;
    showEmptyPageCategories: boolean;
    showEmptyRailCategories: boolean;
    showEmptyDeviceCategories: boolean;
    pageScope: TreemapScope;
    railScope: TreemapScope;
    deviceScope: TreemapScope;
    selectedPageMetric: RailMetricKey;
    selectedRailMetric: RailMetricKey;
    selectedDeviceMetric: RailMetricKey;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};

type TopBrandPoint = {
    thingBrandName: string;
    plays: number;
};

type RecsGenrePlayPoint = {
    genre: string;
    recPlays: number;
    totalPlays: number;
    recPlaySharePct: number;
    recUsers: number;
};

type GenreRisersFallersDatum = RankedBarDatum & {
    percentChange?: number;
};

type ContentPageTableRow = Record<string, any>;
type ContentRailTableRow = Record<string, any>;
type ContentDeviceTableRow = Record<string, any>;
type RailMetricKey = "plays" | "views" | "users" | "avg_progress" | "plays_per_user" | "hours";
type TreemapScope = "top10" | "last10" | "all";

type SourceTreemapNode = {
    name: string;
    value: number;
    rawMetricValue: number;
    sourceLabel: string;
    plays: number;
    views: number;
    users: number;
    avgProgress: number | null;
    playsPerUser: number | null;
    hours: number;
};

type WidgetLoadState = {
    isLoading: boolean;
    error: string | null;
};

type WidgetStateMap = Record<string, WidgetLoadState>;

type CachedWidgetData = {
    statePatch: Partial<ContentGenreExplorerState>;
    sidebarEntry: DocsSidebarEntry | null;
};

const CONTENT_GENRE_WIDGET_KEYS = [
    "content_genre_explorer_metrics",
    "content_genre_explorer_top_brands",
    "recs_genre_plays",
    "genre_explorer_risers_fallers",
    "genre_explorer_metrics_weekly",
    "genre_explorer_metrics_dow",
    "genre_explorer_page_table",
    "genre_explorer_rail_table",
    "genre_explorer_device_table"
];

const RAIL_METRIC_OPTIONS: Array<{ key: RailMetricKey; label: string }> = [
    { key: "plays", label: "Plays" },
    { key: "views", label: "Views" },
    { key: "users", label: "Users" },
    { key: "avg_progress", label: "Avg Progress" },
    { key: "plays_per_user", label: "Plays Per User" },
    { key: "hours", label: "Hours" }
];

const SOURCE_SCOPE_OPTIONS: Array<{ key: TreemapScope; label: string }> = [
    { key: "top10", label: "Top 10" },
    { key: "last10", label: "Last 10" },
    { key: "all", label: "All" }
];

const CHART_PRIMARY_COLOR = Branding.primary;
const CHART_SECONDARY_COLOR = Branding.secondary;
const INDIGO_HUE = 257;

const createInitialWidgetStates = (isLoading: boolean): WidgetStateMap => {
    return CONTENT_GENRE_WIDGET_KEYS.reduce<WidgetStateMap>((acc, widgetKey) => {
        acc[widgetKey] = { isLoading, error: null };
        return acc;
    }, {});
};

class ContentGenreExplorer extends React.Component<ContentGenreExplorerProps, ContentGenreExplorerState> {
    private static readonly TOP_BRANDS_AXIS_WIDTH = 280;
    private static readonly TOP_BRANDS_AXIS_TEXT_MAX_WIDTH = 236;
    private static readonly TREEMAP_HEIGHT = 560;
    private containerRef = React.createRef<HTMLDivElement>();
    private pageRequestId = 0;
    private metricsByTimeRequestId = 0;
    private isMountedFlag = false;
    private widgetDataCache = new Map<string, CachedWidgetData>();

    constructor(props: ContentGenreExplorerProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            isGenresLoading: true,
            isLoading: false,
            error: null,
            widgetStates: createInitialWidgetStates(false),
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filtersCollapsed: false,
            isDarkTheme: true,
            genres: [],
            selectedGenres: [ALL_GENRES_VALUE],
            selectedMetricByTime: getDefaultMetricByTime(),
            activeTimeView: "dow",
            metricsData: [],
            topBrandsData: [],
            recsGenrePlaysData: [],
            risersFallersData: [],
            weeklyMetricsByTimeData: [],
            dowMetricsByTimeData: [],
            pageTableData: [],
            railTableData: [],
            deviceTableData: [],
            isPageTableView: false,
            isRailTableView: false,
            isDeviceTableView: false,
            showEmptyPageCategories: true,
            showEmptyRailCategories: true,
            showEmptyDeviceCategories: true,
            pageScope: "top10",
            railScope: "top10",
            deviceScope: "top10",
            selectedPageMetric: "plays",
            selectedRailMetric: "plays",
            selectedDeviceMetric: "plays",
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.isMountedFlag = true;
        this.initializePage();
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.pageRequestId += 1;
        this.metricsByTimeRequestId += 1;
    }

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private initializePage = async (): Promise<void> => {
        try {
            this.setState({ isGenresLoading: true, error: null });

            const genreResponse = await getGenres(this.props.customerConfig);
            const genreBody = await genreResponse.json();
            const rawGenres: unknown = genreBody?.items?.[0]?.genres;
            const genreOptions = Array.isArray(rawGenres)
                ? rawGenres.filter((genre): genre is string => typeof genre === "string")
                : [];

            const normalizedGenres = [
                ALL_GENRES_VALUE,
                ...genreOptions.filter(genre => genre !== ALL_GENRES_VALUE)
            ];

            const defaultGenres = [ALL_GENRES_VALUE];

            this.setState({
                genres: normalizedGenres,
                selectedGenres: defaultGenres,
                isGenresLoading: false
            }, () => {
                this.fetchExplorerData(defaultGenres);
            });
        } catch (error) {
            Logger.error("Error initializing content genre explorer", error);
            this.setState({
                error: "Failed to load content genre explorer",
                isGenresLoading: false
            });
        }
    };

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { metricsData, topBrandsData, recsGenrePlaysData, risersFallersData, selectedGenres, appliedFilters } = this.state;
        const data: ExportableData[] = [];
        const selectedGenresLabel = this.getSelectedGenresLabel(selectedGenres);

        if (metricsData.length > 0) {
            const row = metricsData[0];
            const metricRows = Object.keys(row)
                .filter(k => !k.endsWith("_percent_change"))
                .map(k => ({ metric: k, value: row[k] }));
            data.push({
                title: `Genre Metrics - ${selectedGenresLabel}`,
                columns: [
                    { key: "metric", label: "Metric" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: metricRows,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (topBrandsData.length > 0) {
            data.push({
                title: `${replaceAnalyticsBrandTerminology("Top Brands", this.props.customerConfig)} - ${selectedGenresLabel}`,
                columns: [
                    { key: "thingBrandName", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "plays", label: "Plays", format: "number" }
                ],
                rows: topBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (recsGenrePlaysData.length > 0) {
            data.push({
                title: "Recommendation Plays by Genre",
                columns: [
                    { key: "genre", label: "Genre" },
                    { key: "recPlays", label: "Rec Plays", format: "number" },
                    { key: "totalPlays", label: "Total Plays", format: "number" },
                    { key: "recPlaySharePct", label: "Rec Play Share", format: "percent" },
                    { key: "recUsers", label: "Rec Users", format: "number" }
                ],
                rows: recsGenrePlaysData,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (risersFallersData.length > 0) {
            data.push({
                title: `${replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig)} - ${selectedGenresLabel}`,
                columns: [
                    { key: "label", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "value", label: "Absolute Change", format: "number" },
                    { key: "percentChange", label: "PoP %", format: "percent" }
                ],
                rows: risersFallersData,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Genre Explorer",
            pageKey: "content_genre_explorer",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private buildSidebarEntry = (widgetKey: string, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(current => current.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private getWidgetErrorMessage = (widgetKey: string, error: unknown): string => {
        const statusCode = typeof (error as { status?: unknown })?.status === "number"
            ? Number((error as { status?: unknown }).status)
            : null;

        if (
            (widgetKey === "genre_explorer_metrics_weekly" || widgetKey === "genre_explorer_metrics_dow")
            && statusCode === 400
        ) {
            return "Invalid metric selection";
        }

        if (statusCode !== null && statusCode >= 500) {
            return "Unable to load data. Please retry.";
        }

        return "Failed to load widget data";
    };

    private updateWidget = async (
        requestId: number,
        requestScope: "page" | "metricsByTime",
        widgetKey: string,
        loadResponse: () => Promise<unknown>,
        applyResponse: (response: unknown) => Partial<ContentGenreExplorerState>,
        cacheKey?: string
    ): Promise<void> => {
        try {
            const cachedData = cacheKey ? this.widgetDataCache.get(cacheKey) : undefined;
            const response = cachedData ? undefined : await loadResponse();
            const latestRequestId = requestScope === "page" ? this.pageRequestId : this.metricsByTimeRequestId;

            if (!this.isMountedFlag || requestId !== latestRequestId) {
                return;
            }

            const statePatch = cachedData?.statePatch ?? applyResponse(response);
            const sidebarEntry = cachedData?.sidebarEntry ?? this.buildSidebarEntry(widgetKey, response);

            if (cacheKey && !cachedData) {
                this.widgetDataCache.set(cacheKey, { statePatch, sidebarEntry });
            }

            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: null }
                } as WidgetStateMap;

                return {
                    ...statePatch,
                    sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                    widgetStates,
                    isLoading: Object.values(widgetStates).some((state: WidgetLoadState) => state.isLoading)
                } as ContentGenreExplorerState;
            });
        } catch (error) {
            const latestRequestId = requestScope === "page" ? this.pageRequestId : this.metricsByTimeRequestId;
            if (!this.isMountedFlag || requestId !== latestRequestId) {
                return;
            }

            Logger.error(`Error fetching ${widgetKey}`, error);
            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: this.getWidgetErrorMessage(widgetKey, error) }
                } as WidgetStateMap;

                return {
                    widgetStates,
                    isLoading: Object.values(widgetStates).some((state: WidgetLoadState) => state.isLoading)
                } as ContentGenreExplorerState;
            });
        }
    };

    private getWidgetCacheKey = (
        widgetKey: string,
        extraParams?: Record<string, string | number | boolean | undefined>
    ): string => {
        const { appliedFilters } = this.state;
        const normalizedFilters = {
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            attributionId: [...appliedFilters.attributionId].sort(),
            brandName: [...appliedFilters.brandName].sort(),
            devicePlatform: [...appliedFilters.devicePlatform].sort()
        };

        return JSON.stringify({
            customer: this.props.customerConfig.slug,
            widgetKey,
            filters: normalizedFilters,
            extraParams: extraParams ?? {}
        });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            this.fetchExplorerData(this.state.selectedGenres);
        });
    };

    private handleMetricByTimeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedMetricByTime = event.target.value;
        this.setState({ selectedMetricByTime }, () => {
            this.fetchActiveMetricsByTimeWidget(this.state.activeTimeView, this.state.selectedGenres, selectedMetricByTime);
        });
    };

    private handleTimeViewChange = (activeTimeView: GenreExplorerTimeView): void => {
        this.setState({ activeTimeView }, () => {
            this.fetchActiveMetricsByTimeWidget(activeTimeView);
        });
    };

    private handleGenreChange = (selection: any[]): void => {
        const selectedValues = selection
            .map(option => typeof option === "string" ? option : String(option?.label ?? ""))
            .filter(value => this.state.genres.includes(value));
        let selectedGenres: string[];

        if (selectedValues.length === 0) {
            selectedGenres = [ALL_GENRES_VALUE];
        } else if (selectedValues.includes(ALL_GENRES_VALUE) && selectedValues.length > 1) {
            selectedGenres = this.state.selectedGenres.includes(ALL_GENRES_VALUE)
                ? selectedValues.filter(value => value !== ALL_GENRES_VALUE)
                : [ALL_GENRES_VALUE];
        } else {
            selectedGenres = selectedValues;
        }

        this.setState({ selectedGenres }, () => {
            this.fetchExplorerData(selectedGenres);
        });
    };

    private getGenreParams = (selectedGenres: string[]): { genre: string } | undefined => {
        if (selectedGenres.length === 0 || selectedGenres.includes(ALL_GENRES_VALUE)) {
            return undefined;
        }

        const normalizedGenres = Array.from(new Set(selectedGenres)).sort((a, b) => a.localeCompare(b));
        return { genre: normalizedGenres.join(",") };
    };

    private getSelectedGenresLabel = (selectedGenres: string[]): string => {
        if (selectedGenres.length === 0 || selectedGenres.includes(ALL_GENRES_VALUE)) {
            return ALL_GENRES_VALUE;
        }

        return Array.from(new Set(selectedGenres)).sort((a, b) => a.localeCompare(b)).join(", ");
    };

    private fetchActiveMetricsByTimeWidget = (
        activeTimeView: GenreExplorerTimeView,
        selectedGenresOverride?: string[],
        selectedMetricByTimeOverride?: string
    ): void => {
        const { appliedFilters } = this.state;
        const selectedGenres = selectedGenresOverride ?? this.state.selectedGenres;
        const selectedMetricByTime = selectedMetricByTimeOverride ?? this.state.selectedMetricByTime;
        const requestId = ++this.metricsByTimeRequestId;
        const widgetKey = activeTimeView === "weekly" ? "genre_explorer_metrics_weekly" : "genre_explorer_metrics_dow";
        const widgetSlug = activeTimeView === "weekly" ? "genre_explorer_metrics_weekly" : "genre_explorer_metrics_dow";
        const widgetParams = {
            "metric_by_time": selectedMetricByTime,
            ...this.getGenreParams(selectedGenres)
        };
        const cacheKey = this.getWidgetCacheKey(widgetKey, widgetParams);

        this.setState(prevState => {
            const widgetStates = {
                ...prevState.widgetStates,
                [widgetKey]: { isLoading: true, error: null }
            } as WidgetStateMap;

            return {
                widgetStates,
                ...(activeTimeView === "weekly"
                    ? { weeklyMetricsByTimeData: [] as GenreExplorerMetricsByTimePoint[] }
                    : { dowMetricsByTimeData: [] as GenreExplorerMetricsByTimePoint[] }),
                isLoading: Object.values(widgetStates).some((state: WidgetLoadState) => state.isLoading)
            } as ContentGenreExplorerState;
        });

        void this.updateWidget(requestId, "metricsByTime", widgetKey, () => getAnalyticsWidget(
            this.props.customerConfig,
            widgetSlug,
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            widgetParams
        ), (response: unknown) => {
            const rows = Array.isArray((response as any)?.result) ? (response as any).result : [];

            if (activeTimeView === "weekly") {
                return {
                    weeklyMetricsByTimeData: mapWeeklyMetricsByTimeRows(rows)
                };
            }

            return {
                dowMetricsByTimeData: mapDowMetricsByTimeRows(rows)
            };
        }, cacheKey);
    };

    private fetchExplorerData = async (selectedGenres: string[]): Promise<void> => {
        const { appliedFilters, selectedMetricByTime } = this.state;
        const requestId = ++this.pageRequestId;
        this.metricsByTimeRequestId += 1;

        this.setState({
            isLoading: true,
            error: null,
            widgetStates: createInitialWidgetStates(true),
            metricsData: [],
            topBrandsData: [],
            recsGenrePlaysData: [],
            risersFallersData: [],
            weeklyMetricsByTimeData: [],
            dowMetricsByTimeData: [],
            pageTableData: [],
            railTableData: [],
            deviceTableData: [],
            sidebarEntries: []
        });

        const genreParams = this.getGenreParams(selectedGenres);
        const widgetParams = {
            "metric_by_time": selectedMetricByTime,
            ...genreParams
        };

        void this.updateWidget(requestId, "page", "content_genre_explorer_metrics", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_explorer_metrics",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => ({
            metricsData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }), this.getWidgetCacheKey("content_genre_explorer_metrics", genreParams));

        void this.updateWidget(requestId, "page", "content_genre_explorer_top_brands", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_explorer_top_brands",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => {
            const topBrandsRaw = (response as any)?.result;
            return {
                topBrandsData: (Array.isArray(topBrandsRaw) ? topBrandsRaw : [])
                    .map((row: Record<string, any>) => ({
                        thingBrandName: String(row?.thing_brand_name || row?.thing_brandname || "Unknown"),
                        plays: this.toNumberOrNull(row?.plays) ?? 0
                    }))
                    .sort((a: TopBrandPoint, b: TopBrandPoint) => b.plays - a.plays)
                    .slice(0, 25)
            };
        }, this.getWidgetCacheKey("content_genre_explorer_top_brands", genreParams));

        void this.updateWidget(requestId, "page", "recs_genre_plays", () => getAnalyticsWidget(
            this.props.customerConfig,
            "recs_genre_plays",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            recsGenrePlaysData: (Array.isArray((response as any)?.result) ? (response as any).result : [])
                .map((row: Record<string, any>): RecsGenrePlayPoint => ({
                    genre: String(row?.genre || "Unknown"),
                    recPlays: this.toNumberOrNull(row?.rec_plays) ?? 0,
                    totalPlays: this.toNumberOrNull(row?.total_plays) ?? 0,
                    recPlaySharePct: this.toNumberOrNull(row?.rec_play_share_pct) ?? 0,
                    recUsers: this.toNumberOrNull(row?.rec_users) ?? 0
                }))
                .sort((a: RecsGenrePlayPoint, b: RecsGenrePlayPoint) => b.recPlays - a.recPlays)
        }), this.getWidgetCacheKey("recs_genre_plays"));

        void this.updateWidget(requestId, "page", "genre_explorer_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => ({
            risersFallersData: (Array.isArray((response as any)?.result) ? (response as any).result : [])
                .map((row: Record<string, any>): GenreRisersFallersDatum | null => {
                    const value = this.toNumberOrNull(row?.video_start_diff);
                    if (value === null) {
                        return null;
                    }

                    const percentChange = this.toNumberOrNull(row?.video_start_pop_pct);
                    const movement = String(row?.movement || "").toLowerCase();
                    return {
                        label: String(row?.brand_name || row?.thing_brandname || row?.thing_brand_name || "Unknown"),
                        value,
                        percentChange: percentChange ?? undefined,
                        fill: movement === "faller" || value < 0 ? "#ff5c7a" : "#4fd1c5"
                    };
                })
                .filter((item: GenreRisersFallersDatum | null): item is GenreRisersFallersDatum => item !== null)
        }), this.getWidgetCacheKey("genre_explorer_risers_fallers", genreParams));

        void this.updateWidget(requestId, "page", "genre_explorer_metrics_weekly", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_metrics_weekly",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            widgetParams
        ), (response: unknown) => {
            const rows = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                weeklyMetricsByTimeData: mapWeeklyMetricsByTimeRows(rows)
            };
        }, this.getWidgetCacheKey("genre_explorer_metrics_weekly", widgetParams));

        void this.updateWidget(requestId, "page", "genre_explorer_metrics_dow", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_metrics_dow",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            widgetParams
        ), (response: unknown) => {
            const rows = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                dowMetricsByTimeData: mapDowMetricsByTimeRows(rows)
            };
        }, this.getWidgetCacheKey("genre_explorer_metrics_dow", widgetParams));

        void this.updateWidget(requestId, "page", "genre_explorer_page_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_page_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => ({
            pageTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }), this.getWidgetCacheKey("genre_explorer_page_table", genreParams));

        void this.updateWidget(requestId, "page", "genre_explorer_rail_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_rail_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => ({
            railTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }), this.getWidgetCacheKey("genre_explorer_rail_table", genreParams));

        void this.updateWidget(requestId, "page", "genre_explorer_device_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "genre_explorer_device_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            genreParams
        ), (response: unknown) => ({
            deviceTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }), this.getWidgetCacheKey("genre_explorer_device_table", genreParams));
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
        return null;
    };

    private getTopBrandsAxisFont = (): string => {
        if (typeof document === "undefined") {
            return "14px sans-serif";
        }

        const bodyStyle = window.getComputedStyle(document.body);
        return bodyStyle.font || `${bodyStyle.fontSize || "14px"} ${bodyStyle.fontFamily || "sans-serif"}`;
    };

    private renderTopBrandsAxisTick = (tickProps: any): ReactNode => {
        const { x, y, payload } = tickProps;
        const fullLabel = String(payload?.value ?? "");
        const axisFont = this.getTopBrandsAxisFont();
        const tickColor = this.state.isDarkTheme ? "#ffffff" : "#212529";
        const truncatedLabel = truncateAxisLabelToWidth(
            fullLabel,
            ContentGenreExplorer.TOP_BRANDS_AXIS_TEXT_MAX_WIDTH,
            axisFont
        );

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dominantBaseline="middle" textAnchor="end" fill={tickColor} style={{ font: axisFont }}>
                    <title>{fullLabel}</title>
                    {truncatedLabel}
                </text>
            </g>
        );
    };

    private getMetricCards = (): MetricCardItem[] => {
        const { metricsData, sidebarEntries } = this.state;
        if (!Array.isArray(metricsData) || metricsData.length === 0) {
            return [];
        }

        const schema = sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.schema as Record<string, { description?: string }> | undefined;
        const sourceRow = metricsData[0] as Record<string, any>;
        const ignoredKeys = new Set([
            "genre",
            "genre_name",
            "genreName",
            "date",
            "datepartition",
            "brand_count"
        ]);

        const candidateKeys = Object.keys(sourceRow)
            .filter(key => !ignoredKeys.has(key))
            .filter(key => !key.startsWith("previous_"))
            .filter(key => !key.endsWith("_diff"))
            .filter(key => !key.endsWith("_pop_Pct"))
            .filter(key => !key.endsWith("_pop_pct"))
            .filter(key => !key.endsWith("_percent_change"))
            .filter(key => !key.endsWith("_change"))
            .filter(key => this.toNumberOrNull(sourceRow[key]) !== null)
            .slice(0, 8);

        return candidateKeys.map((key): MetricCardItem => {
            const value = this.toNumberOrNull(sourceRow[key]);
            const percentChange = this.toNumberOrNull(sourceRow[`${key}_pop_Pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_pop_pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_percent_change`])
                ?? this.toNumberOrNull(sourceRow[`percent_change_${key}`]);
            const explicitNumericChange = this.toNumberOrNull(sourceRow[`${key}_diff`])
                ?? this.toNumberOrNull(sourceRow[`${key}_change`]);
            const previousValue = this.toNumberOrNull(sourceRow[`previous_${key}`]);
            const numericChange = explicitNumericChange
                ?? (value !== null && previousValue !== null ? value - previousValue : null);

            const isPercentMetric = key.includes("percent") || key.includes("rate");
            const isDecimalMetric = key.includes("per_") && !isPercentMetric;

            return {
                key,
                label: this.toTitleCase(key),
                value,
                changePercent: percentChange,
                numericChange,
                valueFormat: isPercentMetric ? "percent" : isDecimalMetric ? "decimal" : "number",
                description: schema?.[key]?.description
            };
        });
    };

    private getBrandBarColor = (index: number, total: number): string => {
        if (total <= 1) {
            return CHART_PRIMARY_COLOR;
        }

        const startHue = 205; // blue
        const endHue = 285; // purple
        const hue = startHue + ((endHue - startHue) * index) / (total - 1);

        return `hsl(${hue}, 78%, 52%)`;
    };

    private getActiveMetricsByTimeWidgetKey = (): string => {
        return this.state.activeTimeView === "weekly"
            ? "genre_explorer_metrics_weekly"
            : "genre_explorer_metrics_dow";
    };

    private getActiveMetricsByTimeData = (): GenreExplorerMetricsByTimePoint[] => {
        return this.state.activeTimeView === "weekly"
            ? this.state.weeklyMetricsByTimeData
            : this.state.dowMetricsByTimeData;
    };

    private formatRecPlayShare = (value: number): string => {
        return `${value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
    };

    private getGenreRecommendationScore = (row: RecsGenrePlayPoint): number => {
        return row.recPlaySharePct * Math.log10(row.recUsers + 1);
    };

    private getRecommendationCategory = (
        recPlaySharePct: number,
        recPlays: number,
        avgShare: number,
        avgRecPlays: number
    ): string => {
        const highShare = recPlaySharePct >= avgShare;
        const highPlays = recPlays >= avgRecPlays;

        if (highShare && highPlays) {
            return "Strongest recommendation genre";
        }

        if (highShare && !highPlays) {
            return "Efficient but niche genre";
        }

        if (!highShare && highPlays) {
            return "High-volume opportunity genre";
        }

        return "Low-priority recommendation genre";
    };

    private getScoreColor = (score: number, minScore: number, maxScore: number): string => {
        const startHue = INDIGO_HUE;
        const endHue = 88; // lime

        if (!Number.isFinite(score) || !Number.isFinite(minScore) || !Number.isFinite(maxScore) || maxScore <= minScore) {
            return `hsl(${startHue}, 78%, 52%)`;
        }

        const normalized = (score - minScore) / (maxScore - minScore);
        const clamped = Math.max(0, Math.min(1, normalized));
        const hue = startHue + ((endHue - startHue) * clamped);
        return `hsl(${hue}, 78%, 52%)`;
    };

    private formatMetricsByTimeValue = (rawValue: number | null): string => {
        const metricOption = getMetricByTimeOption(this.state.selectedMetricByTime);
        return formatMetricByTimeValue(rawValue, metricOption.format);
    };

    private formatMetricsByTimeAxisTick = (value: number): string => {
        const metricOption = getMetricByTimeOption(this.state.selectedMetricByTime);

        if (metricOption.format === "conversion") {
            return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
        }

        if (metricOption.format === "percent") {
            return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
        }

        if (metricOption.format === "hours" || metricOption.format === "ratio") {
            return value.toFixed(2);
        }

        return Math.round(value).toLocaleString();
    };

    private renderMetricsByTimeTable = (chartData: GenreExplorerMetricsByTimePoint[]): ReactNode => {
        const keyLabel = this.state.activeTimeView === "weekly" ? "Week" : "Day";

        return (
            <table className="sr-only">
                <caption>{`${getMetricByTimeOption(this.state.selectedMetricByTime).label} by ${this.state.activeTimeView === "weekly" ? "week" : "day of week"}`}</caption>
                <thead>
                    <tr>
                        <th>{keyLabel}</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {chartData.map(point => (
                        <tr key={`table-row-${point.key}`}>
                            <td>{point.key}</td>
                            <td>{this.formatMetricsByTimeValue(point.value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    private renderMetricsByTimeChart = (): ReactNode => {
        const chartData = this.getActiveMetricsByTimeData();
        const metricOption = getMetricByTimeOption(this.state.selectedMetricByTime);
        const ariaLabel = `${metricOption.label} by ${this.state.activeTimeView === "weekly" ? "week" : "day of week"}`;
        const axisColor = this.state.isDarkTheme ? "#ffffff" : "#212529";
        const axisLineColor = this.state.isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
        const tooltipBackground = this.state.isDarkTheme ? "#1f1f1f" : "#ffffff";
        const tooltipTextColor = this.state.isDarkTheme ? "#ffffff" : "#212529";
        const tooltipBorder = this.state.isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)";
        const chartRows = chartData.map((point: GenreExplorerMetricsByTimePoint) => ({
            key: point.key,
            rawValue: point.value,
            chartValue: toChartMetricValue(point.value, metricOption.format)
        }));

        if (this.state.activeTimeView === "weekly") {
            return (
                <div role="img" aria-label={ariaLabel}>
                    <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                            <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="key"
                                tick={{ fill: axisColor }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                                minTickGap={20}
                            />
                            <YAxis
                                tick={{ fill: axisColor }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                                tickFormatter={this.formatMetricsByTimeAxisTick}
                            />
                            <Tooltip
                                labelFormatter={(label: any) => String(label ?? "")}
                                formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                    return [this.formatMetricsByTimeValue(item?.payload?.rawValue ?? null), metricOption.label];
                                }}
                                contentStyle={{
                                    backgroundColor: tooltipBackground,
                                    border: tooltipBorder,
                                    color: tooltipTextColor
                                }}
                                labelStyle={{ color: tooltipTextColor }}
                                itemStyle={{ color: tooltipTextColor }}
                            />
                            <Line
                                type="monotone"
                                dataKey="chartValue"
                                stroke={CHART_PRIMARY_COLOR}
                                strokeWidth={2}
                                connectNulls={false}
                                dot={{ r: 2, fill: CHART_SECONDARY_COLOR }}
                                activeDot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    {this.renderMetricsByTimeTable(chartData)}
                </div>
            );
        }

        return (
            <div role="img" aria-label={ariaLabel}>
                <ResponsiveContainer width="100%" height={360}>
                    <BarChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                        <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                        <XAxis
                            dataKey="key"
                            tick={{ fill: axisColor }}
                            axisLine={{ stroke: axisLineColor }}
                            tickLine={{ stroke: axisLineColor }}
                        />
                        <YAxis
                            tick={{ fill: axisColor }}
                            axisLine={{ stroke: axisLineColor }}
                            tickLine={{ stroke: axisLineColor }}
                            tickFormatter={this.formatMetricsByTimeAxisTick}
                        />
                        <Tooltip
                            labelFormatter={(label: any) => String(label ?? "")}
                            formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                return [this.formatMetricsByTimeValue(item?.payload?.rawValue ?? null), metricOption.label];
                            }}
                            contentStyle={{
                                backgroundColor: tooltipBackground,
                                border: tooltipBorder,
                                color: tooltipTextColor
                            }}
                            labelStyle={{ color: tooltipTextColor }}
                            itemStyle={{ color: tooltipTextColor }}
                        />
                        <Bar dataKey="chartValue" fill={CHART_PRIMARY_COLOR} />
                    </BarChart>
                </ResponsiveContainer>
                {this.renderMetricsByTimeTable(chartData)}
            </div>
        );
    };

    private applyRankBasedTreemapWeights = (rows: SourceTreemapNode[]): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const sortedRows = [...rows].sort((a, b) => {
            if (b.rawMetricValue !== a.rawMetricValue) {
                return b.rawMetricValue - a.rawMetricValue;
            }

            return a.name.localeCompare(b.name);
        });

        const uniqueMetricValuesDesc = Array.from(new Set(sortedRows.map(row => row.rawMetricValue)));
        const maxLevels = 6;
        const levelCount = Math.min(maxLevels, uniqueMetricValuesDesc.length);
        const rankByValue = new Map<number, number>();
        uniqueMetricValuesDesc.forEach((value, index) => {
            rankByValue.set(value, index + 1);
        });

        return sortedRows.map(row => {
            const denseRank = rankByValue.get(row.rawMetricValue) ?? uniqueMetricValuesDesc.length;
            const level = Math.floor(((denseRank - 1) * levelCount) / uniqueMetricValuesDesc.length) + 1;
            const rankWeight = levelCount - level + 1;

            return {
                ...row,
                value: rankWeight
            };
        });
    };

    private getMetricValueFromRow = (row: Record<string, any>, metricKey: RailMetricKey): number => {
        const rawValue = Number(row[metricKey] ?? 0);
        return Number.isFinite(rawValue) ? rawValue : 0;
    };

    private getRowLabel = (row: Record<string, any>): string => {
        return String(
            row.source_page_rollup
            ?? row.source_page
            ?? row.source_rail
            ?? row.source_rail_rollup
            ?? row.device_platform
            ?? row.page
            ?? row.rail
            ?? row.device
            ?? row.name
            ?? ""
        );
    };

    private getScopedRowsByMetric = <T extends Record<string, any>>(rows: T[], metricKey: RailMetricKey, scope: TreemapScope, showEmptyCategories: boolean): T[] => {
        const rankedRows = rows
            .filter(row => showEmptyCategories || this.getMetricValueFromRow(row, metricKey) !== 0)
            .sort((a, b) => {
                const diff = this.getMetricValueFromRow(b, metricKey) - this.getMetricValueFromRow(a, metricKey);
                if (diff !== 0) {
                    return diff;
                }

                return this.getRowLabel(a).localeCompare(this.getRowLabel(b));
            });

        if (scope === "top10") {
            return rankedRows.slice(0, 10);
        }

        if (scope === "last10") {
            return rankedRows.slice(Math.max(0, rankedRows.length - 10));
        }

        return rankedRows;
    };

    private getPageTreemapData = (rows: ContentPageTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentPageTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourcePage = String(row.source_page_rollup ?? row.source_page ?? `Page ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourcePage,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourcePage,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private getRailTreemapData = (rows: ContentRailTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentRailTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourceRail = String(row.source_rail ?? `Rail ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourceRail.replace(/_/g, " "),
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourceRail,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private getDeviceTreemapData = (rows: ContentDeviceTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentDeviceTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const devicePlatform = String(row.device_platform ?? `Device ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: devicePlatform,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: devicePlatform,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private renderRailTreemap = (data: SourceTreemapNode[], theme: WidgetTheme, metricKey: RailMetricKey): ReactNode => {
        const hasData = data.length > 0;

        if (!hasData) {
            return <Alert variant="info" style={{ marginBottom: 0 }}>No source rail data available</Alert>;
        }

        const maxValue = data.reduce((currentMax, item) => Math.max(currentMax, Number(item.value ?? 0)), 0);
        const metricLabel = RAIL_METRIC_OPTIONS.find(option => option.key === metricKey)?.label || "Metric";
        const formatMetricValue = (value: number): string => {
            if (metricKey === "avg_progress") {
                return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
            }

            return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        };
        const nodeByName = new Map<string, SourceTreemapNode>(data.map(item => [item.name, item]));
        const getNodeFromTreemapPayload = (payload: any, name?: string): SourceTreemapNode | null => {
            const candidates = [
                payload,
                payload?.payload,
                payload?.payload?.payload,
                payload?.data,
                payload?.node,
                payload?.node?.payload
            ];

            for (const candidate of candidates) {
                if (!candidate) {
                    continue;
                }

                const candidatePlays = Number(candidate.plays);
                const candidateName = typeof candidate.name === "string" ? candidate.name : undefined;

                if (Number.isFinite(candidatePlays) || candidateName) {
                    return candidate as SourceTreemapNode;
                }
            }

            if (name && nodeByName.has(name)) {
                return nodeByName.get(name) ?? null;
            }

            return null;
        };

        const truncateLabel = (value: string, width: number): string => {
            const chars = Math.max(8, Math.floor(width / 8));
            if (value.length <= chars) {
                return value;
            }

            return `${value.slice(0, Math.max(0, chars - 1))}…`;
        };

        const treemapHeight = ContentGenreExplorer.TREEMAP_HEIGHT;

        return (
            <ResponsiveContainer width="100%" height={treemapHeight}>
                <Treemap
                    data={data}
                    dataKey="value"
                    stroke={theme.border}
                    aspectRatio={1}
                    content={(props: any): React.ReactElement => {
                        const {
                            x,
                            y,
                            width,
                            height,
                            name,
                            payload
                        } = props;

                        if (width <= 0 || height <= 0) {
                            return <g />;
                        }

                        const node = getNodeFromTreemapPayload(payload, typeof name === "string" ? name : undefined);
                        const metricRaw = Number(node?.rawMetricValue ?? 0);
                        const metricValue = Number.isFinite(metricRaw) ? metricRaw : 0;
                        const valueForColor = Number(node?.value ?? payload?.value ?? 0);
                        const normalized = maxValue > 0 ? Math.max(0, Math.min(1, valueForColor / maxValue)) : 0;
                        const fill = `hsl(${INDIGO_HUE}, 88%, ${60 - (normalized * 28)}%)`;
                        const canRenderLabel = width > 20 && height > 14;
                        const canRenderMetric = width > 90 && height > 54;
                        const centerX = x + (width / 2);
                        const centerY = y + (height / 2);
                        const baseLabel = String(node?.name ?? name ?? "");
                        const label = truncateLabel(baseLabel, Math.max(8, width - 8));
                        const mainFontSize = Math.max(9, Math.min(20, Math.floor(Math.min(width / 6.5, height / 2.4))));
                        const valueFontSize = Math.max(12, Math.min(16, Math.floor(mainFontSize * 0.72)));
                        const clipId = `rail-cell-${Math.round(x)}-${Math.round(y)}-${Math.round(width)}-${Math.round(height)}`;
                        const textY = canRenderMetric ? centerY - (mainFontSize * 0.28) : centerY;

                        return (
                            <g>
                                <rect x={x} y={y} width={width} height={height} style={{ fill, stroke: theme.border, strokeWidth: 1 }} />
                                <clipPath id={clipId}>
                                    <rect x={x + 2} y={y + 2} width={Math.max(0, width - 4)} height={Math.max(0, height - 4)} />
                                </clipPath>
                                {canRenderLabel && (
                                    <g clipPath={`url(#${clipId})`}>
                                        <text
                                            x={centerX}
                                            y={textY}
                                            fill="#ffffff"
                                            fontSize={mainFontSize}
                                            fontWeight={800}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.38)", strokeWidth: 2 }}
                                        >
                                            {label}
                                        </text>
                                        {canRenderMetric && (
                                            <text
                                                x={centerX}
                                                y={centerY + (valueFontSize * 0.95)}
                                                fill="rgba(255,255,255,0.96)"
                                                fontSize={valueFontSize}
                                                fontWeight={700}
                                                textAnchor="middle"
                                                dominantBaseline="central"
                                                style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.32)", strokeWidth: 1.5 }}
                                            >
                                                {`${metricLabel}: ${formatMetricValue(metricValue)}`}
                                            </text>
                                        )}
                                    </g>
                                )}
                            </g>
                        );
                    }}
                >
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.chartBg,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                        itemStyle={{ color: theme.text }}
                        labelStyle={{ color: theme.text }}
                        formatter={(_value: number | string | Array<number | string> | undefined, _name?: string, details?: any) => {
                            const node = details?.payload as SourceTreemapNode | undefined;
                            if (!node) {
                                return ["", ""];
                            }

                            const avgProgress = node.avgProgress === null ? "-" : `${node.avgProgress.toFixed(2)}%`;
                            const playsPerUser = node.playsPerUser === null ? "-" : node.playsPerUser.toLocaleString(undefined, { maximumFractionDigits: 2 });
                            const selectedMetric = formatMetricValue(Number(node.rawMetricValue ?? 0));

                            return [
                                `${metricLabel}: ${selectedMetric} | Plays: ${node.plays.toLocaleString()} | Views: ${node.views.toLocaleString()} | Users: ${node.users.toLocaleString()} | Avg Progress: ${avgProgress} | Plays/User: ${playsPerUser} | Hours: ${node.hours.toLocaleString()}`,
                                node.name
                            ];
                        }}
                    />
                </Treemap>
            </ResponsiveContainer>
        );
    };

    private renderPageWidget = (theme: WidgetTheme): ReactNode => {
        const { pageTableData, sidebarEntries, widgetStates, isPageTableView, selectedPageMetric, pageScope, showEmptyPageCategories } = this.state;
        const widgetState = widgetStates.genre_explorer_page_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(pageTableData, selectedPageMetric, pageScope, showEmptyPageCategories);
        const treemapData = this.getPageTreemapData(filteredRows, selectedPageMetric);

        return (
            <WidgetCard
                title="Source Page Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "genre_explorer_page_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No source page table data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedPageMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedPageMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={pageScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ pageScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-page-categories"
                            label="Show empty categories"
                            checked={showEmptyPageCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyPageCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isPageTableView: !prevState.isPageTableView }))}
                        >
                            {isPageTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isPageTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No source page table data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_page_rollup", "source_page", "page"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedPageMetric)
                )}
            </WidgetCard>
        );
    };

    private renderRailWidget = (theme: WidgetTheme): ReactNode => {
        const { railTableData, sidebarEntries, widgetStates, isRailTableView, selectedRailMetric, railScope, showEmptyRailCategories } = this.state;
        const widgetState = widgetStates.genre_explorer_rail_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(railTableData, selectedRailMetric, railScope, showEmptyRailCategories);
        const treemapData = this.getRailTreemapData(filteredRows, selectedRailMetric);

        return (
            <WidgetCard
                title="Source Rail Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "genre_explorer_rail_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No source rail data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedRailMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedRailMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={railScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ railScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-rail-categories"
                            label="Show empty categories"
                            checked={showEmptyRailCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyRailCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isRailTableView: !prevState.isRailTableView }))}
                        >
                            {isRailTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isRailTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No source rail data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_rail", "source_rail_rollup", "rail"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedRailMetric)
                )}
            </WidgetCard>
        );
    };

    private renderDeviceWidget = (theme: WidgetTheme): ReactNode => {
        const { deviceTableData, sidebarEntries, widgetStates, isDeviceTableView, selectedDeviceMetric, deviceScope, showEmptyDeviceCategories } = this.state;
        const widgetState = widgetStates.genre_explorer_device_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(deviceTableData, selectedDeviceMetric, deviceScope, showEmptyDeviceCategories);
        const treemapData = this.getDeviceTreemapData(filteredRows, selectedDeviceMetric);

        return (
            <WidgetCard
                title="Device Platform Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "genre_explorer_device_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No device platform data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedDeviceMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedDeviceMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={deviceScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ deviceScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-device-categories"
                            label="Show empty categories"
                            checked={showEmptyDeviceCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyDeviceCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isDeviceTableView: !prevState.isDeviceTableView }))}
                        >
                            {isDeviceTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isDeviceTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No device platform data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["device_platform", "platform", "device"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedDeviceMetric)
                )}
            </WidgetCard>
        );
    };

    public render(): ReactNode {
        const {
            filters,
            filterOptions,
            filtersCollapsed,
            appliedFilters,
            genres,
            selectedGenres,
            selectedMetricByTime,
            activeTimeView,
            isGenresLoading,
            isLoading,
            error,
            topBrandsData,
            recsGenrePlaysData,
            risersFallersData,
            isDarkTheme,
            sidebarOpen,
            sidebarEntries,
            widgetStates
        } = this.state;
        const metricCards = this.getMetricCards();
        const theme = getWidgetTheme(isDarkTheme);
        const allWidgetsSettled = ["content_genre_explorer_metrics", "content_genre_explorer_top_brands", "recs_genre_plays", "genre_explorer_risers_fallers"].every(
            widgetKey => !widgetStates[widgetKey]?.isLoading
        );
        const activeMetricsWidgetKey = this.getActiveMetricsByTimeWidgetKey();
        const activeMetricsByTimeData = this.getActiveMetricsByTimeData();
        const selectedMetricOption = getMetricByTimeOption(selectedMetricByTime);

        if (isGenresLoading) {
            return (
                <Container className="mw-100" key="genre-explorer" style={{ paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Genre Explorer</h2>
                        </Col>
                    </Row>
                </Container>
            );
        }

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container ref={this.containerRef} className="mw-100" key="genre-explorer" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <DataFreshnessBanner customerConfig={this.props.customerConfig} isDarkTheme={isDarkTheme} />
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={allWidgetsSettled && !this.state.error && this.state.metricsData.length > 0}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleTheme}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                    >
                        {isDarkTheme ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={filtersCollapsed ? "Show filters" : "Hide filters"}
                    >
                        {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                    </Button>
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>
                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2 style={{ color: theme.text }}>Genre Explorer</h2>
                    </Col>
                </Row>

                <div
                    style={{
                        position: "sticky",
                        top: 56,
                        zIndex: 100,
                        backgroundColor: theme.background,
                        boxShadow: `0 4px 8px ${isDarkTheme ? "rgba(0, 0, 0, 0.35)" : "rgba(0, 0, 0, 0.08)"}`
                    }}
                >
                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                        isCollapsed={filtersCollapsed}
                        sticky={false}
                    />
                    <Row
                        aria-label="Active genre"
                        style={{
                            marginTop: filtersCollapsed ? 0 : "8px",
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            borderTop: filtersCollapsed ? undefined : `1px solid ${theme.border}`
                        }}
                    >
                        <Col md={4}>
                            <Form.Group style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Form.Label htmlFor="content-genre-explorer-genre" style={{ marginBottom: 0, whiteSpace: "nowrap", fontWeight: 600 }}>
                                    Active genre
                                </Form.Label>
                                <Typeahead
                                    id="content-genre-explorer-genre"
                                    multiple
                                    options={genres}
                                    selected={selectedGenres}
                                    placeholder="Select genres..."
                                    onChange={this.handleGenreChange}
                                    disabled={genres.length === 0 || isLoading || isGenresLoading}
                                    inputProps={{ "aria-label": "Active genre" }}
                                    style={{ flex: 1 }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <WidgetCard
                            title="Genre Metrics"
                            description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.description}
                            theme={theme}
                            isLoading={widgetStates.content_genre_explorer_metrics.isLoading}
                            error={widgetStates.content_genre_explorer_metrics.error}
                            isEmpty={!widgetStates.content_genre_explorer_metrics.isLoading && !widgetStates.content_genre_explorer_metrics.error && metricCards.length === 0}
                            emptyMessage="No numeric metrics available to render as KPI widgets."
                        >
                            <MetricCards
                                metrics={metricCards}
                                theme={theme}
                                combineChangeAndDelta={true}
                                noChangeLabel="—"
                            />
                        </WidgetCard>
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        <WidgetCard
                            title={replaceAnalyticsBrandTerminology("Top Brands by Plays", this.props.customerConfig)}
                            description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_top_brands")?.description}
                            theme={theme}
                            isLoading={widgetStates.content_genre_explorer_top_brands.isLoading}
                            error={widgetStates.content_genre_explorer_top_brands.error}
                            isEmpty={!widgetStates.content_genre_explorer_top_brands.isLoading && !widgetStates.content_genre_explorer_top_brands.error && topBrandsData.length === 0}
                            emptyMessage={replaceAnalyticsBrandTerminology("No top brand data available for this genre and date range.", this.props.customerConfig)}
                        >
                            <ResponsiveContainer width="100%" height={Math.max(560, topBrandsData.length * 28)}>
                                <BarChart
                                    data={topBrandsData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 24, left: 12, bottom: 5 }}
                                >
                                    <CartesianGrid
                                        stroke={isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}
                                        strokeDasharray="3 3"
                                    />
                                    <XAxis
                                        type="number"
                                        tick={{ fill: theme.text }}
                                        axisLine={{ stroke: isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" }}
                                        tickLine={{ stroke: isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" }}
                                        tickFormatter={(value: number) => value.toLocaleString()}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="thingBrandName"
                                        width={ContentGenreExplorer.TOP_BRANDS_AXIS_WIDTH}
                                        interval={0}
                                        tick={this.renderTopBrandsAxisTick}
                                        axisLine={{ stroke: isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" }}
                                        tickLine={{ stroke: isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" }}
                                    />
                                    <Tooltip
                                        formatter={(value: number | string | undefined) => [Number(value ?? 0).toLocaleString(), "Plays"]}
                                        contentStyle={{
                                            backgroundColor: isDarkTheme ? "#1f1f1f" : "#ffffff",
                                            border: isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)",
                                            color: theme.text
                                        }}
                                        labelStyle={{ color: theme.text }}
                                        itemStyle={{ color: theme.text }}
                                    />
                                    <Bar dataKey="plays">
                                        {topBrandsData.map((entry: TopBrandPoint, index: number) => (
                                            <Cell
                                                key={`brand-bar-${entry.thingBrandName}-${index}`}
                                                fill={this.getBrandBarColor(index, topBrandsData.length)}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </WidgetCard>
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        <WidgetCard
                            title="Recommendation Quality by Genre"
                            description={sidebarEntries.find(e => e.widgetKey === "recs_genre_plays")?.description}
                            theme={theme}
                            isLoading={widgetStates.recs_genre_plays.isLoading}
                            error={widgetStates.recs_genre_plays.error}
                            isEmpty={!widgetStates.recs_genre_plays.isLoading && !widgetStates.recs_genre_plays.error && recsGenrePlaysData.length === 0}
                            emptyMessage="No recommendation genre play data available for this date range."
                        >
                            {(() => {
                                const avgShare = recsGenrePlaysData.length > 0
                                    ? recsGenrePlaysData.reduce((sum, row) => sum + row.recPlaySharePct, 0) / recsGenrePlaysData.length
                                    : 0;
                                const avgRecPlays = recsGenrePlaysData.length > 0
                                    ? recsGenrePlaysData.reduce((sum, row) => sum + row.recPlays, 0) / recsGenrePlaysData.length
                                    : 0;
                                const maxShare = Math.max(0, ...recsGenrePlaysData.map(row => row.recPlaySharePct));
                                const maxRecPlays = Math.max(0, ...recsGenrePlaysData.map(row => row.recPlays));
                                const shareDomainMax = Math.max(10, Math.ceil(maxShare * 1.15 / 5) * 5);
                                const recPlaysDomainMax = Math.max(100, Math.ceil(maxRecPlays * 1.15 / 100) * 100);
                                const rankedRows = [...recsGenrePlaysData].sort((a, b) => this.getGenreRecommendationScore(b) - this.getGenreRecommendationScore(a));
                                const scoredData = recsGenrePlaysData.map(row => {
                                    const score = this.getGenreRecommendationScore(row);
                                    return {
                                        ...row,
                                        score
                                    };
                                });
                                const minScore = Math.min(...scoredData.map(row => row.score));
                                const maxScore = Math.max(...scoredData.map(row => row.score));
                                const colorByGenre = new Map(scoredData.map(row => [row.genre, this.getScoreColor(row.score, minScore, maxScore)]));
                                const axisLineColor = isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";

                                return (
                                    <>
                                    <div
                                        style={{
                                            backgroundColor: theme.chartBg,
                                            padding: "16px 16px 6px 16px",
                                            borderRadius: "5px",
                                            marginBottom: "2px",
                                            width: "95%",
                                            marginLeft: "auto",
                                            marginRight: "auto",
                                            position: "relative"
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "14px",
                                                right: "18px",
                                                zIndex: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "3px",
                                                color: theme.text,
                                                fontSize: "13px",
                                                fontWeight: 600
                                            }}
                                        >
                                            <span>Recommendation impact score</span>
                                            <WidgetInfoButton
                                                placement="left"
                                                description={`Bubble color and size show recommendation impact, based on success and users.\n\n**Range:** ${minScore.toFixed(1)} (low) to ${maxScore.toFixed(1)} (high)\n\n**Formula:** (Recommendation Plays / Total Plays) × log10(Rec Users + 1)`}
                                            />
                                        </div>
                                        <ResponsiveContainer width="100%" height={620}>
                                            <ScatterChart margin={{ top: 52, right: 52, left: 72, bottom: 20 }}>
                                                <CartesianGrid stroke="rgba(120, 120, 120, 0.22)" strokeDasharray="3 3" />
                                                <XAxis
                                                    type="number"
                                                    dataKey="recPlaySharePct"
                                                    name="Recommendation Plays / Total Plays"
                                                    domain={[0, shareDomainMax]}
                                                    tickFormatter={(value: number) => this.formatRecPlayShare(value)}
                                                    label={{ value: "Recommendation Plays / Total Plays", position: "bottom", offset: 8, style: { fill: theme.text, fontSize: 14 } }}
                                                    tick={{ fill: theme.text, fontSize: 12 }}
                                                    axisLine={{ stroke: axisLineColor }}
                                                    tickLine={{ stroke: axisLineColor }}
                                                />
                                                <YAxis
                                                    type="number"
                                                    dataKey="recPlays"
                                                    name="Recommendation Plays"
                                                    domain={[0, recPlaysDomainMax]}
                                                    tickFormatter={(value: number) => value.toLocaleString()}
                                                    label={{ value: "Recommendation Plays", angle: -90, position: "left", offset: 8, style: { fill: theme.text, fontSize: 14 } }}
                                                    tick={{ fill: theme.text, fontSize: 12 }}
                                                    axisLine={{ stroke: axisLineColor }}
                                                    tickLine={{ stroke: axisLineColor }}
                                                />
                                                <ZAxis type="number" dataKey="score" range={[140, 780]} name="Score" />
                                                <ReferenceLine x={avgShare} stroke="#a3a3a3" strokeDasharray="4 4" />
                                                <ReferenceLine y={avgRecPlays} stroke="#a3a3a3" strokeDasharray="4 4" />
                                                <Tooltip
                                                    content={({ active, payload }: any) => {
                                                        if (!active || !payload || payload.length === 0) {
                                                            return null;
                                                        }

                                                        const point = payload?.[0]?.payload as (RecsGenrePlayPoint & { score: number }) | undefined;
                                                        if (!point) {
                                                            return null;
                                                        }

                                                        const category = this.getRecommendationCategory(
                                                            point.recPlaySharePct,
                                                            point.recPlays,
                                                            avgShare,
                                                            avgRecPlays
                                                        );

                                                        return (
                                                            <div
                                                                style={{
                                                                    backgroundColor: isDarkTheme ? "#1f1f1f" : "#ffffff",
                                                                    border: isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)",
                                                                    borderRadius: "4px",
                                                                    padding: "10px",
                                                                    maxWidth: "320px",
                                                                    color: theme.text
                                                                }}
                                                            >
                                                                <p style={{ margin: 0, fontWeight: 700 }}>{point.genre}</p>
                                                                <p style={{ margin: "8px 0 0 0" }}>Recommendation Plays / Total Plays (X): {this.formatRecPlayShare(point.recPlaySharePct)}</p>
                                                                <p style={{ margin: "4px 0 0 0" }}>Recommendation Plays (Y): {point.recPlays.toLocaleString()}</p>
                                                                <p style={{ margin: "4px 0 0 0" }}>Rec Users: {point.recUsers.toLocaleString()}</p>
                                                                <p style={{ margin: "4px 0 0 0" }}>Score: {point.score.toFixed(1)}</p>
                                                                <p style={{ margin: "8px 0 0 0", fontWeight: 600 }}>{category}</p>
                                                            </div>
                                                        );
                                                    }}
                                                />
                                                <Scatter name="Genres" data={scoredData} isAnimationActive={false}>
                                                    {scoredData.map((row, index: number) => (
                                                        <Cell
                                                            key={`genre-bubble-${row.genre}-${index}`}
                                                            fill={this.getScoreColor(row.score, minScore, maxScore)}
                                                            fillOpacity={0.8}
                                                        />
                                                    ))}
                                                    <LabelList dataKey="genre" position="top" fill={theme.text} fontSize={11} />
                                                </Scatter>
                                            </ScatterChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <details
                                        style={{
                                            backgroundColor: theme.chartBg,
                                            border: `1px solid ${theme.border}`,
                                            borderRadius: "6px",
                                            marginBottom: "12px",
                                            width: "95%",
                                            marginLeft: "auto",
                                            marginRight: "auto"
                                        }}
                                    >
                                        <summary
                                            style={{
                                                color: theme.text,
                                                cursor: "pointer",
                                                fontWeight: 700,
                                                padding: "12px"
                                            }}
                                        >
                                            Category Guide by Quadrant
                                        </summary>
                                        <div style={{ padding: "0 12px 12px" }}>
                                            <div style={{ color: theme.text, fontSize: "12px", marginBottom: "8px", opacity: 0.85 }}>
                                                Dashed lines mark averages: X = {this.formatRecPlayShare(avgShare)}, Y = {avgRecPlays.toLocaleString()}.
                                            </div>
                                            <table style={{ width: "100%", borderCollapse: "collapse", color: theme.text, fontSize: "13px" }}>
                                                <thead>
                                                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                        <th style={{ padding: "8px", textAlign: "left" }}>Quadrant</th>
                                                        <th style={{ padding: "8px", textAlign: "left" }}>Rule</th>
                                                        <th style={{ padding: "8px", textAlign: "left" }}>Category</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                        <td style={{ padding: "8px" }}>Top-right</td>
                                                        <td style={{ padding: "8px" }}>Recommendation Plays / Total Plays &gt;= {this.formatRecPlayShare(avgShare)} and Recommendation Plays &gt;= {avgRecPlays.toLocaleString()}</td>
                                                        <td style={{ padding: "8px", fontWeight: 600 }}>Strongest recommendation genre</td>
                                                    </tr>
                                                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                        <td style={{ padding: "8px" }}>Bottom-right</td>
                                                        <td style={{ padding: "8px" }}>Recommendation Plays / Total Plays &gt;= {this.formatRecPlayShare(avgShare)} and Recommendation Plays &lt; {avgRecPlays.toLocaleString()}</td>
                                                        <td style={{ padding: "8px", fontWeight: 600 }}>Efficient but niche genre</td>
                                                    </tr>
                                                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                        <td style={{ padding: "8px" }}>Top-left</td>
                                                        <td style={{ padding: "8px" }}>Recommendation Plays / Total Plays &lt; {this.formatRecPlayShare(avgShare)} and Recommendation Plays &gt;= {avgRecPlays.toLocaleString()}</td>
                                                        <td style={{ padding: "8px", fontWeight: 600 }}>High-volume opportunity genre</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: "8px" }}>Bottom-left</td>
                                                        <td style={{ padding: "8px" }}>Recommendation Plays / Total Plays &lt; {this.formatRecPlayShare(avgShare)} and Recommendation Plays &lt; {avgRecPlays.toLocaleString()}</td>
                                                        <td style={{ padding: "8px", fontWeight: 600 }}>Low-priority recommendation genre</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>

                                    <div
                                        style={{
                                            backgroundColor: theme.chartBg,
                                            padding: "15px",
                                            borderRadius: "5px",
                                            marginBottom: "20px",
                                            overflowX: "auto"
                                        }}
                                    >
                                        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
                                            <colgroup>
                                                <col style={{ width: "36%" }} />
                                                <col style={{ width: "10%" }} />
                                                <col style={{ width: "24%" }} />
                                                <col style={{ width: "20%" }} />
                                                <col style={{ width: "10%" }} />
                                            </colgroup>
                                            <thead>
                                                <tr style={{ backgroundColor: theme.chartBg }}>
                                                    <th style={{ padding: "10px", textAlign: "left", backgroundColor: theme.chartBg, borderBottom: `1px solid ${theme.border}` }}>Genre</th>
                                                    <th style={{ padding: "10px", textAlign: "right", backgroundColor: theme.chartBg, borderBottom: `1px solid ${theme.border}` }}>Score</th>
                                                    <th style={{ padding: "10px", textAlign: "right", backgroundColor: theme.chartBg, borderBottom: `1px solid ${theme.border}` }}>Recommendation Plays / Total Plays</th>
                                                    <th style={{ padding: "10px", textAlign: "right", backgroundColor: theme.chartBg, borderBottom: `1px solid ${theme.border}` }}>Recommendation Plays</th>
                                                    <th style={{ padding: "10px", textAlign: "right", backgroundColor: theme.chartBg, borderBottom: `1px solid ${theme.border}` }}>Rec Users</th>
                                                </tr>
                                            </thead>
                                        </table>

                                        <div style={{ maxHeight: "320px", overflowY: "auto", overflowX: "hidden" }}>
                                            <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
                                                <colgroup>
                                                    <col style={{ width: "36%" }} />
                                                    <col style={{ width: "10%" }} />
                                                    <col style={{ width: "24%" }} />
                                                    <col style={{ width: "20%" }} />
                                                    <col style={{ width: "10%" }} />
                                                </colgroup>
                                                <tbody>
                                                    {rankedRows.map((row: RecsGenrePlayPoint, index: number) => (
                                                        <tr key={`genre-breakdown-${row.genre}-${index}`} style={{ backgroundColor: theme.chartBg }}>
                                                            <td style={{ padding: "10px", borderBottom: `1px solid ${theme.border}` }}>
                                                                <span
                                                                    style={{
                                                                        display: "inline-block",
                                                                        width: "10px",
                                                                        height: "10px",
                                                                        borderRadius: "50%",
                                                                        background: colorByGenre.get(row.genre) || this.getBrandBarColor(index, recsGenrePlaysData.length),
                                                                        marginRight: "8px"
                                                                    }}
                                                                />
                                                                {row.genre}
                                                            </td>
                                                            <td style={{ padding: "10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>{this.getGenreRecommendationScore(row).toFixed(1)}</td>
                                                            <td style={{ padding: "10px", textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${theme.border}` }}>{this.formatRecPlayShare(row.recPlaySharePct)}</td>
                                                            <td style={{ padding: "10px", textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${theme.border}` }}>{row.recPlays.toLocaleString()}</td>
                                                            <td style={{ padding: "10px", textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${theme.border}` }}>{row.recUsers.toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    </>
                                );
                            })()}
                        </WidgetCard>
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        <RankedBarWidget
                            title={replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig)}
                            description={sidebarEntries.find(e => e.widgetKey === "genre_explorer_risers_fallers")?.description}
                            theme={theme}
                            data={risersFallersData}
                            isLoading={widgetStates.genre_explorer_risers_fallers.isLoading}
                            error={widgetStates.genre_explorer_risers_fallers.error}
                            emptyMessage={replaceAnalyticsBrandTerminology("No risers/fallers data available for this genre and date range.", this.props.customerConfig)}
                            tooltipLabelFormatter={(label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`}
                            tooltipValueFormatter={(value: number, item: RankedBarDatum) => {
                                const percentChange = (item as GenreRisersFallersDatum).percentChange;
                                const popText = typeof percentChange === "number"
                                    ? ` | PoP: ${percentChange > 0 ? "+" : ""}${percentChange.toFixed(2)}%`
                                    : "";

                                return [`${value.toLocaleString()}${popText}`, "Absolute Change"];
                            }}
                            xAxisScaleMode="sqrt"
                            presentation="diverging"
                            divergingMargin={{ left: 240, right: 240 }}
                            divergingLabelMaxWidth={330}
                        />
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        <WidgetCard
                            title={`${selectedMetricOption.label} by ${activeTimeView === "weekly" ? "Week" : "Day of Week"}`}
                            description={sidebarEntries.find(e => e.widgetKey === activeMetricsWidgetKey)?.description}
                            theme={theme}
                            isLoading={widgetStates[activeMetricsWidgetKey]?.isLoading}
                            error={widgetStates[activeMetricsWidgetKey]?.error}
                            isEmpty={!widgetStates[activeMetricsWidgetKey]?.isLoading && !widgetStates[activeMetricsWidgetKey]?.error && activeMetricsByTimeData.length === 0}
                            emptyMessage="No data for selected filters. Try widening date range or removing filters."
                        >
                            <Row style={{ marginBottom: "12px" }}>
                                <Col md={6}>
                                    <Form.Group style={{ marginBottom: 0 }}>
                                        <Form.Label>Metric</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={selectedMetricByTime}
                                            onChange={this.handleMetricByTimeChange}
                                            disabled={widgetStates[activeMetricsWidgetKey]?.isLoading}
                                        >
                                            {METRIC_BY_TIME_OPTIONS.map(option => (
                                                <option key={option.key} value={option.key}>{option.label}</option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={{ marginBottom: 0 }}>
                                        <Form.Label>View</Form.Label>
                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                            <Button
                                                variant={activeTimeView === "weekly" ? "primary" : "outline-secondary"}
                                                onClick={() => this.handleTimeViewChange("weekly")}
                                                aria-pressed={activeTimeView === "weekly"}
                                                disabled={widgetStates.genre_explorer_metrics_weekly.isLoading}
                                            >
                                                Weekly
                                            </Button>
                                            <Button
                                                variant={activeTimeView === "dow" ? "primary" : "outline-secondary"}
                                                onClick={() => this.handleTimeViewChange("dow")}
                                                aria-pressed={activeTimeView === "dow"}
                                                disabled={widgetStates.genre_explorer_metrics_dow.isLoading}
                                            >
                                                Day of Week
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            {this.renderMetricsByTimeChart()}
                        </WidgetCard>
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        {this.renderPageWidget(theme)}
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        {this.renderRailWidget(theme)}
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        {this.renderDeviceWidget(theme)}
                    </Col>
                </Row>

                <ScheduleExportModal
                    show={this.state.showScheduleModal}
                    onHide={() => this.setState({ showScheduleModal: false })}
                    filters={this.state.appliedFilters}
                    pageKey="content_genre_explorer"
                    pageTitle="Genre Explorer"
                    customerSlug={this.props.customerConfig.slug}
                    customerConfig={this.props.customerConfig}
                    editingSchedule={this.state.editingSchedule}
                    onSave={this.handleScheduleSave}
                />
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default ContentGenreExplorer;
